import { memo } from 'react'
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native'
import { Modal, Portal, Text, useTheme } from 'react-native-paper'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { useTimeEntryAudits } from '@/hooks/useTimeEntryAudits'
import { BREAKPOINTS } from '@/lib/config'
import { Button } from '@/ui/Button'
import { LoadingIndicator } from '@/ui/LoadingIndicator'

interface AuditHistoryDialogProps {
    visible: boolean
    timeEntryId: string
    onDismiss: () => void
}

const FIELD_LABELS: Record<string, string> = {
    job: 'Job',
    location: 'Location',
    clock_in: 'Clock In',
    clock_out: 'Clock Out',
    duration: 'Duration',
    notes: 'Notes',
    status: 'Status',
    entry_type: 'Type',
}

function formatFieldValue(field: string, value: unknown): string {
    if (value === null || value === undefined || value === '') return '(empty)'
    if (field === 'duration') {
        const seconds = Number(value)
        if (Number.isNaN(seconds)) return String(value)
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        if (hours > 0) return `${hours}h ${minutes}m`
        return `${minutes}m`
    }
    if (field === 'clock_in' || field === 'clock_out') {
        return formatDateTimeValue(value)
    }
    return String(value)
}

function formatDateTimeValue(value: unknown, timeOnly = false): string {
    if (value === null || value === undefined || value === '') return '(empty)'
    try {
        const d = new Date(String(value))
        if (timeOnly) {
            return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        }
        return d.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        })
    } catch {
        return String(value)
    }
}

function sameDay(a: unknown, b: unknown): boolean {
    if (!a || !b) return false
    try {
        const da = new Date(String(a))
        const db = new Date(String(b))
        return (
            da.getFullYear() === db.getFullYear() &&
            da.getMonth() === db.getMonth() &&
            da.getDate() === db.getDate()
        )
    } catch {
        return false
    }
}

function formatChangePair(
    field: string,
    change: { old: unknown; new: unknown }
): { old: string; new: string } {
    if (field === 'clock_in' || field === 'clock_out') {
        const timeOnly = sameDay(change.old, change.new)
        return {
            old: formatDateTimeValue(change.old, timeOnly),
            new: formatDateTimeValue(change.new, timeOnly),
        }
    }
    return {
        old: formatFieldValue(field, change.old),
        new: formatFieldValue(field, change.new),
    }
}

function formatTimestamp(value: string): string {
    try {
        const d = new Date(value)
        return d.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        })
    } catch {
        return value
    }
}

interface FlatRow {
    key: string
    date: string
    user: string
    field: string
    oldValue: string
    newValue: string
    note?: string
    isFirstInGroup: boolean
    isLastInGroup: boolean
}

type AuditRecord = {
    id: string
    action: string
    changed_at: string
    changedByName: string
    changes?: Record<string, { old: unknown; new: unknown }>
    notes?: string
}

function getFilteredChanges(audit: AuditRecord) {
    const changes = audit.changes as Record<string, { old: unknown; new: unknown }> | undefined
    if (!changes) return []
    return Object.entries(changes).filter(([field, change]) => {
        return formatFieldValue(field, change.old) !== formatFieldValue(field, change.new)
    })
}

function getActionLabel(action: string): string {
    if (action === 'created') return 'Entry created'
    return action.charAt(0).toUpperCase() + action.slice(1)
}

function buildRows(audits: AuditRecord[]): FlatRow[] {
    const rows: FlatRow[] = []
    for (const audit of audits) {
        const changeEntries = getFilteredChanges(audit)
        const timestamp = formatTimestamp(audit.changed_at)

        if (changeEntries.length === 0) {
            rows.push({
                key: `${audit.id}-action`,
                date: timestamp,
                user: audit.changedByName,
                field: getActionLabel(audit.action),
                oldValue: '',
                newValue: '',
                note: audit.notes,
                isFirstInGroup: true,
                isLastInGroup: true,
            })
            continue
        }

        for (let i = 0; i < changeEntries.length; i++) {
            const [field, change] = changeEntries[i]
            const formatted = formatChangePair(field, change)
            const isFirst = i === 0
            const isLast = i === changeEntries.length - 1
            rows.push({
                key: `${audit.id}-${field}`,
                date: isFirst ? timestamp : '',
                user: isFirst ? audit.changedByName : '',
                field: FIELD_LABELS[field] ?? field,
                oldValue: formatted.old,
                newValue: formatted.new,
                note: isLast ? audit.notes : undefined,
                isFirstInGroup: isFirst,
                isLastInGroup: isLast,
            })
        }
    }
    return rows
}

export const AuditHistoryDialog = memo(function AuditHistoryDialog({
    visible,
    timeEntryId,
    onDismiss,
}: AuditHistoryDialogProps) {
    const theme = useTheme()
    useDismissOnEscape(visible, onDismiss)
    const { width, height } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg
    const { audits, isLoading } = useTimeEntryAudits(visible ? timeEntryId : null)
    const rows = buildRows(audits)

    const headerColor = theme.colors.onSurfaceVariant
    const borderColor = theme.colors.outlineVariant

    return (
        <Portal>
            <Modal
                visible={visible}
                onDismiss={onDismiss}
                contentContainerStyle={[
                    styles.modalContainer,
                    {
                        backgroundColor: theme.colors.surface,
                        width: isDesktop ? 700 : width - 40,
                        maxHeight: height * 0.8,
                    },
                ]}
            >
                <View style={styles.header}>
                    <Text variant="titleLarge" style={styles.title}>
                        Change History
                    </Text>
                    <Button title="✕" onPress={onDismiss} mode="text" />
                </View>
                <ScrollView
                    horizontal
                    style={styles.content}
                    contentContainerStyle={styles.horizontalScroll}
                >
                    <ScrollView
                        style={styles.verticalScroll}
                        contentContainerStyle={styles.contentInner}
                    >
                        {isLoading ? (
                            <LoadingIndicator />
                        ) : rows.length === 0 ? (
                            <Text
                                variant="bodyMedium"
                                style={{ color: headerColor, textAlign: 'center' }}
                            >
                                No change history available
                            </Text>
                        ) : isDesktop ? (
                            <AuditTable
                                rows={rows}
                                headerColor={headerColor}
                                borderColor={borderColor}
                            />
                        ) : (
                            <MobileAuditList audits={audits} borderColor={borderColor} />
                        )}
                    </ScrollView>
                </ScrollView>
            </Modal>
        </Portal>
    )
})

interface AuditTableProps {
    rows: FlatRow[]
    headerColor: string
    borderColor: string
}

const AuditTable = memo(function AuditTable({ rows, headerColor, borderColor }: AuditTableProps) {
    const theme = useTheme()

    return (
        <View>
            <View
                style={[styles.tableRow, styles.tableHeaderRow, { borderBottomColor: borderColor }]}
            >
                <Text variant="labelSmall" style={[styles.colDate, { color: headerColor }]}>
                    Date
                </Text>
                <Text variant="labelSmall" style={[styles.colUser, { color: headerColor }]}>
                    User
                </Text>
                <Text variant="labelSmall" style={[styles.colField, { color: headerColor }]}>
                    Changed
                </Text>
                <Text variant="labelSmall" style={[styles.colValue, { color: headerColor }]}>
                    Old Value
                </Text>
                <Text variant="labelSmall" style={[styles.colValue, { color: headerColor }]}>
                    New Value
                </Text>
            </View>
            {rows.map(row => (
                <View key={row.key}>
                    <View
                        style={[
                            styles.tableRow,
                            row.isFirstInGroup &&
                                !rows[0]?.key.startsWith(row.key) && {
                                    borderTopWidth: 1,
                                    borderTopColor: borderColor,
                                    paddingTop: 10,
                                },
                            row.isLastInGroup && { paddingBottom: row.note ? 2 : 10 },
                        ]}
                    >
                        <Text
                            variant="bodySmall"
                            style={[styles.colDate, { color: headerColor }]}
                            numberOfLines={1}
                        >
                            {row.date}
                        </Text>
                        <Text variant="bodySmall" style={styles.colUser} numberOfLines={1}>
                            {row.user}
                        </Text>
                        <Text
                            variant="bodySmall"
                            style={[styles.colField, { color: headerColor }]}
                            numberOfLines={1}
                        >
                            {row.field}
                        </Text>
                        <Text
                            variant="bodySmall"
                            style={[styles.colValue, { color: headerColor }]}
                            numberOfLines={1}
                        >
                            {row.oldValue}
                        </Text>
                        <Text
                            variant="bodySmall"
                            style={[
                                styles.colValue,
                                styles.newValue,
                                { color: theme.colors.primary },
                            ]}
                            numberOfLines={1}
                        >
                            {row.newValue}
                        </Text>
                    </View>
                    {row.note ? (
                        <Text variant="bodySmall" style={[styles.noteText, { color: headerColor }]}>
                            Note: {row.note}
                        </Text>
                    ) : null}
                </View>
            ))}
        </View>
    )
})

interface MobileAuditListProps {
    audits: AuditRecord[]
    borderColor: string
}

const MobileAuditList = memo(function MobileAuditList({
    audits,
    borderColor,
}: MobileAuditListProps) {
    const theme = useTheme()
    const labelColor = theme.colors.onSurfaceVariant

    return (
        <View>
            {audits.map((audit, index) => {
                const changeEntries = getFilteredChanges(audit)
                const hasChanges = changeEntries.length > 0
                return (
                    <View
                        key={audit.id}
                        style={[
                            styles.mobileGroup,
                            index > 0 && { borderTopWidth: 1, borderTopColor: borderColor },
                        ]}
                    >
                        <View style={styles.mobileGroupHeader}>
                            <Text variant="labelMedium" style={{ color: theme.colors.onSurface }}>
                                {formatTimestamp(audit.changed_at)}
                            </Text>
                            <Text variant="bodySmall" style={{ color: labelColor }}>
                                {audit.changedByName}
                            </Text>
                        </View>
                        <View style={[styles.mobileDivider, { borderBottomColor: borderColor }]} />
                        {!hasChanges && (
                            <Text
                                variant="bodySmall"
                                style={{ color: labelColor, fontStyle: 'italic' }}
                            >
                                {getActionLabel(audit.action)}
                            </Text>
                        )}
                        {changeEntries.map(([field, change]) => {
                            const formatted = formatChangePair(field, change)
                            return (
                                <View key={field} style={styles.mobileTableRow}>
                                    <Text
                                        variant="bodySmall"
                                        style={[styles.mColField, { color: labelColor }]}
                                        numberOfLines={1}
                                    >
                                        {FIELD_LABELS[field] ?? field}
                                    </Text>
                                    <Text
                                        variant="bodySmall"
                                        style={[styles.mColValue, { color: labelColor }]}
                                        numberOfLines={1}
                                    >
                                        {formatted.old}
                                    </Text>
                                    <Text
                                        variant="bodySmall"
                                        style={[
                                            styles.mColValue,
                                            { color: theme.colors.primary, fontWeight: '600' },
                                        ]}
                                        numberOfLines={1}
                                    >
                                        {formatted.new}
                                    </Text>
                                </View>
                            )
                        })}
                        {audit.notes ? (
                            <Text
                                variant="bodySmall"
                                style={{ color: labelColor, fontStyle: 'italic', marginTop: 4 }}
                            >
                                Note: {audit.notes}
                            </Text>
                        ) : null}
                    </View>
                )
            })}
        </View>
    )
})

const COL_DATE = 130
const COL_USER = 110
const COL_FIELD = 85
const COL_VALUE = 90

const styles = StyleSheet.create({
    modalContainer: {
        margin: 20,
        borderRadius: 12,
        alignSelf: 'center',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 8,
    },
    title: {
        flex: 1,
    },
    content: {
        flexShrink: 1,
    },
    horizontalScroll: {
        minWidth: '100%',
    },
    verticalScroll: {
        flexShrink: 1,
    },
    contentInner: {
        paddingHorizontal: 24,
        paddingTop: 4,
        paddingBottom: 24,
    },
    tableRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        paddingVertical: 4,
        gap: 8,
    },
    tableHeaderRow: {
        borderBottomWidth: 1,
        paddingBottom: 8,
        marginBottom: 4,
    },
    colDate: {
        width: COL_DATE,
        flexShrink: 0,
    },
    colUser: {
        width: COL_USER,
        flexShrink: 0,
    },
    colField: {
        width: COL_FIELD,
        flexShrink: 0,
    },
    colValue: {
        width: COL_VALUE,
        flexShrink: 0,
    },
    newValue: {
        fontWeight: '600',
    },
    noteText: {
        fontStyle: 'italic',
        paddingLeft: COL_DATE + COL_USER + 16,
        paddingBottom: 8,
    },
    mobileGroup: {
        paddingVertical: 12,
        gap: 4,
    },
    mobileGroupHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    mobileDivider: {
        borderBottomWidth: 1,
        marginTop: 4,
        marginBottom: 2,
    },
    mobileTableRow: {
        flexDirection: 'row',
        paddingVertical: 3,
        gap: 6,
    },
    mColField: {
        width: 70,
        flexShrink: 0,
    },
    mColValue: {
        flex: 1,
        textAlign: 'right',
    },
})
